import { ALL_KEYS } from "@app-utils/oems/createColumnDefs";
import { restServer } from "@app-utils/restServer";
import useController from "@app-utils/useController";
import { OrderColumnInfo, OrdersTableMetadata } from "@enfusion-ui/core";
import { useMounted, useRefCallback } from "@enfusion-ui/hooks";
import { noop, pick } from "lodash";
import queryString from "query-string";
import React from "react";

import { InspectorOEMSConfig, useInspectorContext } from "../inspector/context";
import {
  initialOEMSContextState,
  OEMSBlotterRowsTransactionContext,
  OEMSContext,
  OEMSContextState,
  OemsTransactions,
} from "./context";

const prevOverrideKeys = ["orderColumns", "metadata"];
export const OEMSProvider: React.FC = ({ children }) => {
  const { enableModule, disableModule, subscribeMessage, messages } =
    useController("OEMS");

  const [omsState, setOMSStateBase] = React.useState<OEMSContextState>(
    initialOEMSContextState
  );
  const [transactions, setTransactions] = React.useState<OemsTransactions>({
    action: "",
    callback: noop,
  });

  const isMounted = useMounted();
  const { config: configBase, environment } = useInspectorContext();
  const config = configBase as InspectorOEMSConfig;
  const { username } = config;

  const getInitialMetadata = useRefCallback(
    async (
      initializationMessage = "",
      overrideState: Partial<OEMSContextState> = {}
    ) => {
      const applicationSessionStorageKey = ["applicationSessionToken", username]
        .filter(Boolean)
        .join("///");
      const applicationSessionToken = sessionStorage.getItem(
        applicationSessionStorageKey
      );
      const metadata = (await restServer(
        queryString.stringifyUrl({
          url: "/api/oms/v1/orders/table/metadata",
        }),
        environment.restAPIUrl,
        {
          headers: {
            "X-Auth-Token": `${applicationSessionToken}`,
          },
        }
      )) as unknown as OrdersTableMetadata | undefined;

      setOMSState((prevState) => ({
        ...prevState,
        ...overrideState,
        orderColumns: ALL_KEYS.map((i: any) => ({
          key: i,
        })) as OrderColumnInfo[],
        metadata,
        initializationMessage,
      }));
    },
    [username, environment]
  );

  const initializeOEMS = useRefCallback(
    (secureToken?: string) => {
      enableModule(username, secureToken, environment.restAPIUrl);
    },
    [username, enableModule, environment]
  );

  React.useEffect(() => {
    return subscribeMessage((msg: Record<string, any>) => {
      const { command, payload } = msg;
      switch (command) {
        case "init-status": {
          const { message, completed } = payload;
          if (completed) {
            getInitialMetadata(message);
          } else {
            setOMSState((prevState) => ({
              ...prevState,
              initializationStep: (prevState.initializationStep ?? 0) + 1,
              initializationMessage: message,
            }));
          }
          break;
        }
        case "blotter-sync": {
          const { rows, isInitialized } = payload;
          setOMSState((prevState) => ({ ...prevState, isInitialized }));
          setTransactions({
            add: rows,
            action: "blotter-sync",
          });
          break;
        }
        case "blotter-rows-update": {
          setTransactions({
            add: payload.add,
            update: payload.update,
            action: "blotter-rows-update",
          });

          break;
        }
        case "blotter-row-update": {
          const { newOrder, row } = payload;

          if (newOrder) {
            setTransactions({
              add: [row],
              action: "blotter-row-update",
            });
          } else {
            setTransactions({
              action: "blotter-row-update",
              update: [row],
            });
          }

          break;
        }
        case "order-delete": {
          const { rowToDelete } = payload;
          setTransactions({
            action: "order-delete",
            remove: [rowToDelete],
          });
          break;
        }
        case "close": {
          setOMSState((prevState) => ({
            ...prevState,
            ...initialOEMSContextState,
            ...pick(prevState, prevOverrideKeys),
          }));
          break;
        }
        case "terminated": {
          setOMSState((prevState) => ({
            ...prevState,
            ...initialOEMSContextState,
            ...pick(prevState, prevOverrideKeys),
          }));
          break;
        }
      }
    });
  }, [subscribeMessage, transactions]);

  const setOMSState = useRefCallback(
    (action: React.SetStateAction<OEMSContextState>) => {
      if (isMounted()) {
        setOMSStateBase(action);
      }
    },
    [isMounted]
  );
  const restartSocket = useRefCallback(
    async (secureToken?: string) => {
      disableModule();
      enableModule(username, secureToken);
    },
    [disableModule, enableModule]
  );

  const closeSocket = useRefCallback(() => {
    disableModule();
  }, []);

  return (
    <OEMSContext.Provider
      value={{
        ...omsState,
        restartSocket,
        initializeOEMS,
        closeSocket,
        messages,
      }}
    >
      <OEMSBlotterRowsTransactionContext.Provider value={transactions}>
        {children}
      </OEMSBlotterRowsTransactionContext.Provider>
    </OEMSContext.Provider>
  );
};
